import type { NivelQualidade } from './qualidadeGrafica'

/**
 * Contador de quadros por segundo, opt-in por endereço (`?fps=1`).
 *
 * Existe porque o FPS real só se mede na máquina de quem usa: o ambiente de
 * desenvolvimento renderiza por software e não serve. A parte pura (resumo das
 * amostras e texto) fica aqui; o componente que lê o renderizador e escreve na
 * tela é `ContadorFps`.
 */
export function contadorFpsDaBusca(busca: string): boolean {
  const valor = new URLSearchParams(busca).get('fps')
  return valor === '1' || valor === 'true'
}

/** Quantos quadros recentes entram no resumo. Com 60 quadros por segundo, ~1,5 s. */
export const QUADROS_NO_RESUMO = 90

/**
 * Intervalos maiores que isto não são quadro lento, são a aba em segundo plano
 * ou a janela parada: contá-los derrubaria a média sem dizer nada da cena.
 */
export const INTERVALO_MAXIMO_MS = 1000

export interface ResumoDeQuadros {
  readonly quadros: number
  readonly fps: number
  readonly msMedio: number
  /** 95% dos quadros levaram até este tempo: aponta engasgos que a média esconde */
  readonly msP95: number
  readonly msPior: number
}

/** Resume os intervalos entre quadros (em ms); `null` sem nenhuma amostra. */
export function resumirQuadros(intervalosMs: readonly number[]): ResumoDeQuadros | null {
  const validos = intervalosMs.filter((ms) => ms > 0 && ms <= INTERVALO_MAXIMO_MS)
  if (validos.length === 0) return null
  const ordenados = [...validos].sort((a, b) => a - b)
  const msMedio = validos.reduce((soma, ms) => soma + ms, 0) / validos.length
  return {
    quadros: validos.length,
    fps: 1000 / msMedio,
    msMedio,
    msP95: ordenados[Math.min(ordenados.length - 1, Math.ceil(ordenados.length * 0.95) - 1)]!,
    msPior: ordenados[ordenados.length - 1]!,
  }
}

/** Acrescenta uma amostra mantendo só as `QUADROS_NO_RESUMO` mais recentes. */
export function registrarIntervalo(amostras: number[], ms: number): void {
  amostras.push(ms)
  if (amostras.length > QUADROS_NO_RESUMO) amostras.shift()
}

export interface LeituraDoRenderizador {
  /** chamadas de desenho do quadro inteiro (sombras e passagens extras incluídas) */
  readonly chamadas: number
  readonly triangulos: number
  readonly dpr: number
  readonly qualidade: NivelQualidade
}

const MILHARES = new Intl.NumberFormat('pt-BR')

/** Linhas exibidas no contador. */
export function linhasDoContador(resumo: ResumoDeQuadros | null, leitura: LeituraDoRenderizador): string[] {
  const decimal = (n: number, casas = 1) => n.toFixed(casas).replace('.', ',')
  return [
    resumo
      ? `${decimal(resumo.fps, 0)} FPS · ${decimal(resumo.msMedio)} ms`
      : 'medindo…',
    resumo ? `p95 ${decimal(resumo.msP95)} ms · pior ${decimal(resumo.msPior)} ms` : '',
    `${MILHARES.format(leitura.chamadas)} chamadas · ${MILHARES.format(leitura.triangulos)} tri`,
    `qualidade ${leitura.qualidade} · DPR ${decimal(leitura.dpr, 2)}`,
  ].filter((linha) => linha !== '')
}
